import { useCallback, useRef } from 'react';
import { isEmpty, isEqual, mapValues, omit } from 'lodash-es';

import { logger } from 'client/utils/logger/logger';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { authenticatedSaveAppraisalAction } from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import {
  getAllTmvConditionPricing,
  getAllConditionTotalPrices,
} from 'site-modules/shared/utils/appraisal/get-condition-pricing';
import { STORAGE_KEYS } from 'site-modules/shared/constants/car-buying';
import {
  TRADE_IN_VALUE,
  SAVE_ESTIMATED_PAYMENTS_TRACKING,
  PARTNER_OFFER_TRACKING_CREATIVE_ID,
  APPRAISAL_PAGE_CREATIVE_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';
import {
  APPRAISAL_VALUE_NAME,
  PAYOFF_AMOUNT_NAME,
  APPLIED_TRADE_IN_NAME,
  TRADE_IN_NAME,
} from 'client/site-modules/shared/constants/calculator/calculator';
import {
  setVehicleId,
  generateIdmDataToSave,
  getMultiOfferDataToIdm,
  getInstantOfferDataToIdm,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-idm';
import { getTotalPrices } from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';

/**
 * Encapsulates all IDM save/sync logic: generating payloads, persisting to profile,
 * and coordinating the partner-offer eligibility state.
 *
 * @param {Object} params
 * @param {Function} params.setState - state setter
 * @param {Object} params.props - full component props
 * @param {Object} params.vehicleIdRef - ref holding the current vehicle UUID
 * @param {Object} params.lastSavedStyleIdRef - ref holding the last style id that was saved
 * @param {Object} params.storageRef - ref to the localStorage Storage instance
 * @param {Function} params.getVehicleMMY - returns { make, model, year } for the current vehicle
 * @param {Object} params.tmvConditionsResponseData - TMV conditions response from the model
 * @param {boolean} params.isExtendedReport - partner-offer flow flag
 * @param {boolean|null} params.isAuthenticated - auth state (null = unknown)
 * @param {Object} params.pendingCallbacksRef - ref queue for post-commit callbacks
 * @param {Object} params.stateRef - ref always pointing at the current state (avoids state in dep arrays)
 */
export function useIdmPersistence({
  setState,
  props,
  vehicleIdRef,
  lastSavedStyleIdRef,
  storageRef,
  getVehicleMMY,
  tmvConditionsResponseData,
  isExtendedReport,
  isAuthenticated,
  pendingCallbacksRef,
  stateRef,
}) {
  const { createOrUpdateIdmData, isHiddenForRenewal, isRenewalSetupReady = true } = props;
  const lastAppraisalSaveRef = useRef(null);

  const ensureVehicleId = useCallback(() => {
    const styleId = stateRef.current?.vehicle?.style?.id;
    setVehicleId(vehicleIdRef, lastSavedStyleIdRef, styleId);
  }, [vehicleIdRef, lastSavedStyleIdRef, stateRef]);

  const setLocalStorage = useCallback(
    ({ tradeIn }) => {
      const vehicleMMY = getVehicleMMY();
      const storage = storageRef.current;

      storage.set(STORAGE_KEYS.LEASE_PREFERENCES, {
        ...storage.get(STORAGE_KEYS.LEASE_PREFERENCES),
        ...vehicleMMY,
        tradeIn,
      });

      storage.set(STORAGE_KEYS.LOAN_PREFERENCES, {
        ...storage.get(STORAGE_KEYS.LOAN_PREFERENCES),
        ...vehicleMMY,
        tradeIn,
      });
    },
    [getVehicleMMY, storageRef]
  );

  const generateIdmDataToSaveLocal = useCallback(
    (allConditionTotalPrices, includePii, offerPrice = null) =>
      generateIdmDataToSave({
        allConditionTotalPrices,
        includePii,
        offerPrice,
        state: stateRef.current,
        vehicleId: vehicleIdRef.current,
        props,
      }),
    [props, stateRef, vehicleIdRef]
  );

  const saveDataToIdm = useCallback(
    async idmDataToSave => {
      const isDataSaved = await createOrUpdateIdmData({
        idmDataToSave,
        signInAnonymouslyTracking: { creativeId: APPRAISAL_PAGE_CREATIVE_ID },
      })
        .then(() => true)
        .catch(e => {
          logger.warn("Profile Appraisal data wasn't saved properly", e);
          return false;
        });
      return isDataSaved;
    },
    [createOrUpdateIdmData]
  );

  const saveAppraiserDataToIdm = useCallback(() => {
    if (isHiddenForRenewal || !isRenewalSetupReady) return;

    const { tmvConditions } = tmvConditionsResponseData;

    if (isExtendedReport && tmvConditions != null) {
      delayHeavyTask(() => {
        const { options, vehicle, condition } = stateRef.current;

        if (isEmpty(vehicle.style)) return;

        const allConditionPricing = getAllTmvConditionPricing(tmvConditions, options);
        const allConditionTotalPrices = getAllConditionTotalPrices(allConditionPricing);

        ensureVehicleId();
        const INCLUDE_PII_FALSE = false;
        const totalPrices = getTotalPrices(allConditionTotalPrices, condition);

        if (isEmpty(totalPrices)) return;

        setLocalStorage({ tradeIn: totalPrices[TRADE_IN_VALUE] });

        if (isAuthenticated !== null) {
          const idmDataToSave = generateIdmDataToSaveLocal(allConditionTotalPrices, INCLUDE_PII_FALSE);
          // TMV updates and VIN inventory checks can save the same appraisal. Compare the
          // persisted data without the timestamps regenerated for each save attempt.
          const appraisalData = {
            ...idmDataToSave,
            vehicles: {
              ...idmDataToSave.vehicles,
              appraisalHistory: mapValues(idmDataToSave.vehicles?.appraisalHistory, appraisal =>
                omit(appraisal, ['savedTs', 'offerEligibilityExpiryTs'])
              ),
            },
          };
          if (isEqual(lastAppraisalSaveRef.current, appraisalData)) return;

          lastAppraisalSaveRef.current = appraisalData;
          saveDataToIdm(idmDataToSave).then(isDataSaved => {
            if (!isDataSaved && lastAppraisalSaveRef.current === appraisalData) {
              lastAppraisalSaveRef.current = null;
            }
          });
          authenticatedSaveAppraisalAction(totalPrices[TRADE_IN_VALUE]);
        }
      });
    }
  }, [
    tmvConditionsResponseData,
    isHiddenForRenewal,
    isRenewalSetupReady,
    isExtendedReport,
    stateRef,
    ensureVehicleId,
    setLocalStorage,
    isAuthenticated,
    generateIdmDataToSaveLocal,
    saveDataToIdm,
  ]);

  const saveMultiOfferAndInstantOfferDataToIdm = useCallback(
    async (multiOfferData, instantOfferData) => {
      ensureVehicleId();
      const multiOfferDataToSave = getMultiOfferDataToIdm(multiOfferData, vehicleIdRef.current, props.isAuthenticated);
      const appraisalOffersDataToSave = getInstantOfferDataToIdm({
        instantOfferData,
        vehicleId: vehicleIdRef.current,
        state: stateRef.current,
        props,
        generateIdmDataToSaveFn: generateIdmDataToSaveLocal,
      });

      if (multiOfferDataToSave || appraisalOffersDataToSave) {
        const isDataSaved = await saveDataToIdm({ ...appraisalOffersDataToSave, ...multiOfferDataToSave });

        if (isDataSaved) {
          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
            event_data: {
              action_name: TrackingConstant.ACTION_RECEIVE_EPO,
              subaction_name: 'mods_data_profile_update',
              creative_id: PARTNER_OFFER_TRACKING_CREATIVE_ID,
              action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
              action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
            },
          });
        }
      }
    },
    [ensureVehicleId, vehicleIdRef, props, stateRef, generateIdmDataToSaveLocal, saveDataToIdm]
  );

  const saveEstimatedPayments = useCallback(() => {
    delayHeavyTask(() => {
      const { allConditionTotalPrices, payoffAmount, appliedTradeIn, vehicle, condition } = stateRef.current;
      const { onTradeInUpdate, onVehicleUpdate, onDone } = props;
      const totalPrices = getTotalPrices(allConditionTotalPrices, condition);
      const tradeIn = totalPrices[TRADE_IN_VALUE];
      const INCLUDE_PII_TRUE = true;
      const totalPriceWithAmount = tradeIn + payoffAmount;

      ensureVehicleId();
      const idmDataToSave = generateIdmDataToSaveLocal(allConditionTotalPrices, INCLUDE_PII_TRUE);

      setLocalStorage({ tradeIn: totalPriceWithAmount });

      if (isAuthenticated !== null) {
        saveDataToIdm(idmDataToSave);
        authenticatedSaveAppraisalAction(totalPriceWithAmount);
      }

      if (onTradeInUpdate) {
        onTradeInUpdate(TRADE_IN_NAME, totalPriceWithAmount, getVehicleMMY(), {
          [APPRAISAL_VALUE_NAME]: tradeIn,
          [PAYOFF_AMOUNT_NAME]: Math.abs(payoffAmount),
          [APPLIED_TRADE_IN_NAME]: appliedTradeIn,
        });
      }

      if (onVehicleUpdate) onVehicleUpdate(vehicle);

      EventToolbox.fireTrackAction({
        ...SAVE_ESTIMATED_PAYMENTS_TRACKING,
        event_data: {
          ...SAVE_ESTIMATED_PAYMENTS_TRACKING.event_data,
          value: totalPriceWithAmount,
        },
      });

      if (onDone) onDone();
    });
  }, [
    stateRef,
    props,
    ensureVehicleId,
    generateIdmDataToSaveLocal,
    setLocalStorage,
    isAuthenticated,
    saveDataToIdm,
    getVehicleMMY,
  ]);

  const setPartnerOfferVinEligibilityWithSave = useCallback(
    (isPartnerOfferEligibleVin, isPartnerOfferVinEligibilityCheckSkipped) => {
      if (!isPartnerOfferVinEligibilityCheckSkipped && !isPartnerOfferEligibleVin) {
        pendingCallbacksRef.current.push(() => {
          saveAppraiserDataToIdm();
        });
      }
      setState(prev => ({ ...prev, isPartnerOfferEligibleVin }));
    },
    [pendingCallbacksRef, saveAppraiserDataToIdm, setState]
  );

  return {
    saveAppraiserDataToIdm,
    saveMultiOfferAndInstantOfferDataToIdm,
    saveEstimatedPayments,
    setPartnerOfferVinEligibilityWithSave,
  };
}
